import { useState } from "react";
import { toast } from "sonner";
import { Edit, Trash2 } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Skeleton as LoadingSkeleton } from "@/components/ui/skeleton";
import { useFetchSaccoRepaymentsForLoan, useDeleteSaccoLoan } from "@saccoHooks";
import { LoanForm } from "./LoanForm";
import {SaccoLoan} from "@/@types/sacco.models.ts";

interface LoanCardProps {
  loan: SaccoLoan;
  membershipId: string;
  nativeCurrency?: string;
  displayCurrency?: string;
  convert?: (amount: number, from: string, to: string) => number;
  isLoading?: boolean;
  error?: Error | null;
}

function formatCurrency(value: number, currency: string) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(value);
}

export function LoanCard({
  loan,
  membershipId,
  nativeCurrency = 'KES',
  displayCurrency = 'KES',
  convert,
  isLoading: ratesLoading = false,
  error: ratesError = null,
}: LoanCardProps) {
  const [editOpen, setEditOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const { data: repayments = [], isLoading } = useFetchSaccoRepaymentsForLoan(loan.id);
  const deleteLoan = useDeleteSaccoLoan(membershipId);

  const effectiveCurrency = ratesError ? nativeCurrency : displayCurrency;

  function convertAmt(raw: number): number {
    if (ratesError || ratesLoading || !convert || displayCurrency === nativeCurrency) return raw;
    try {
      return convert(raw, nativeCurrency, displayCurrency);
    } catch {
      return raw;
    }
  }

  const paidRepayments = repayments.filter((r) => r.is_paid === true);
  const unpaidRepayments = repayments.filter((r) => r.is_paid === false);

  const paidCount = paidRepayments.length;
  const remainingCount = unpaidRepayments.length;
  const outstandingBalance = unpaidRepayments.reduce(
    (sum, r) => sum + Number(r.installment_amount),
    0
  );
  const totalInterestPaid = paidRepayments.reduce(
    (sum, r) => sum + Number(r.interest_portion),
    0
  );
  const totalPrincipalPaid = paidRepayments.reduce(
    (sum, r) => sum + Number(r.principal_portion),
    0
  );

  const progressPercent =
    loan.total_installments > 0
      ? (paidCount / loan.total_installments) * 100
      : 0;

  async function handleConfirmDelete() {
    try {
      await deleteLoan.mutateAsync(loan.id);
      toast.success("Loan deleted successfully");
    } catch {
      toast.error("Failed to delete loan");
    }
  }

  return (
    <>
      <Card className="w-full">
        <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
          <div className="space-y-1">
            <CardTitle className="text-base font-semibold">
              Loan — {loan.disbursement_date}
            </CardTitle>
            <Badge variant={loan.status === "active" ? "default" : "secondary"}>
              {loan.status}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {isLoading ? (
            <div className="space-y-2">
              <LoadingSkeleton className="h-4 w-full" />
              <LoadingSkeleton className="h-4 w-3/4" />
              <LoadingSkeleton className="h-2 w-full" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Principal:</span>
                  <span className="font-medium">
                    {ratesLoading ? (
                      <LoadingSkeleton className="h-4 w-20 inline-block" />
                    ) : (
                      formatCurrency(convertAmt(Number(loan.principal)), effectiveCurrency)
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Outstanding Balance:</span>
                  <span className="font-medium text-red-600">
                    {ratesLoading ? (
                      <LoadingSkeleton className="h-4 w-20 inline-block" />
                    ) : (
                      formatCurrency(convertAmt(outstandingBalance), effectiveCurrency)
                    )}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Principal Paid:</span>
                  <span className="font-medium text-green-600">
                    {formatCurrency(convertAmt(totalPrincipalPaid), effectiveCurrency)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Interest Paid:</span>
                  <span className="font-medium">
                    {formatCurrency(convertAmt(totalInterestPaid), effectiveCurrency)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Installments:</span>
                  <span className="font-medium">
                    {paidCount} paid / {remainingCount} remaining
                  </span>
                </div>
              </div>

              <div className="space-y-1">
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>Progress</span>
                  <span>{Math.round(progressPercent)}%</span>
                </div>
                <Progress value={progressPercent} className="h-2" />
              </div>
            </>
          )}

          <div className="flex gap-2 pt-2">
            <Button
              variant="outline"
              size="sm"
              className="flex-1"
              onClick={() => setEditOpen(true)}
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="flex-1 text-red-600 hover:text-red-700"
              onClick={() => setDeleteDialogOpen(true)}
              disabled={deleteLoan.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Delete
            </Button>
          </div>
        </CardContent>
      </Card>

      <LoanForm
        open={editOpen}
        onOpenChange={setEditOpen}
        membershipId={membershipId}
        loan={loan}
      />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Loan</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this loan of{" "}
              <strong>{formatCurrency(Number(loan.principal), nativeCurrency)}</strong>? This
              will permanently remove the loan and all its repayment records.
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
